import type { SxProps, Theme } from "@mui/material";
import { Box, Checkbox, Divider, Grid, Stack, Typography } from "@mui/material";
import { Fragment, useEffect, useMemo, useRef } from "react";
import { makeTintedBackgroundFromColor } from "@entrilia/shared/utilities/colorHelper.ts";
import type { CustomMeasureReturnType } from "../../customMeasure/hooks/useCustomMeasure";
import type { FormulaNode } from "../../customMeasure/utilities/fillNodes";
import { findNodeByTokenData } from "../../customMeasure/utilities/findNodeByTokenData";
import type { ChartOfAccountsContextItem } from "../contexts/ChartOfAccountsContext";
import { useChartOfAccountsContext } from "../contexts/ChartOfAccountsContext";
import { getVariablePropertyItemUniqueId } from "../helpers/chartOfAccountsContextHelper";
import { getEmptyConditionsCaption } from "../helpers/variablePropertyItem.helper";
import Filter from "./Filter";
import PropertyItemContainer from "./PropertyItemContainer";
import { PropertyName } from "./PropertyName";
import "./VariablePropertyItem.css";

interface Props {
  formulaNode: FormulaNode;
  state: CustomMeasureReturnType;
  sx?: SxProps<Theme>;
  index: number;
  parentNode: ChartOfAccountsContextItem | null;
}

export const VariablePropertyItem = ({ formulaNode, state, sx, parentNode, index }: Props) => {
  const { root, getNode, onSelect, appendChild } = useChartOfAccountsContext();
  const id = getVariablePropertyItemUniqueId(index, formulaNode, parentNode?.identifier);

  const currentNode = useMemo(() => {
    return getNode(id, parentNode);
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [parentNode, id, root]);

  const formulaContainerRef = useRef<HTMLDivElement>(null);

  const chartOfAccountItems = useMemo(() => {
    return state.chartOfAccounts?.gl.concat(state.chartOfAccounts?.memo || []) || [];
  }, [state.chartOfAccounts?.gl, state.chartOfAccounts?.memo]);

  const conditionsAreEmpty = !formulaNode.conditions || formulaNode.conditions?.length === 0;

  const isSelectedParameter = useMemo(() => {
    if (state.selectedFormulaParameter === undefined) return false;

    return formulaNode.linkedNodes.some((ln) => findNodeByTokenData(ln, state.selectedFormulaParameter));
  }, [state.selectedFormulaParameter, formulaNode]);

  useEffect(() => {
    if (isSelectedParameter) {
      formulaContainerRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [isSelectedParameter]);

  useEffect(() => {
    if (parentNode) {
      appendChild(id, parentNode);
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [parentNode, id]);

  return (
    <Grid container component="li" sx={{ flexWrap: "nowrap", width: "100%" }}>
      <Stack sx={{ flex: 1, width: "100%", ...sx }}>
        <PropertyItemContainer
          sx={formulaNode.highlighted === true ? { border: "1px solid", borderColor: "primary.main" } : {}}
          ref={formulaContainerRef}
        >
          <Box
            sx={(theme) => ({
              display: "flex",
              justifyContent: "space-between",
              height: "28px",
              px: 1,
              borderTopLeftRadius: "4px",
              borderTopRightRadius: "4px",
              bgcolor: currentNode?.checked
                ? makeTintedBackgroundFromColor(theme, theme.palette.primary.main)
                : theme.palette.background.paper,
            })}
          >
            <Grid
              container
              sx={{
                alignContent: "center",
                alignItems: "center",
                gap: 1.25,
                flexWrap: "nowrap",
                width: "100%",
              }}
            >
              {currentNode && (
                <Checkbox
                  indeterminate={currentNode.indeterminate === true}
                  checked={currentNode.checked === true}
                  sx={{ width: "28px", height: "28px" }}
                  onChange={(e) => {
                    onSelect(currentNode.identifier, e.target.checked);
                  }}
                />
              )}
              <PropertyName text={formulaNode.text} />
            </Grid>
          </Box>
          {conditionsAreEmpty && (
            <>
              <Divider />
              <Grid container sx={{ px: 2, height: "30px", width: "100%", alignItems: "center" }}>
                <Typography variant="caption">{getEmptyConditionsCaption(state.isConfigurable)}</Typography>
              </Grid>
            </>
          )}
          {formulaNode.conditions?.length !== 0 &&
            formulaNode.conditions?.map((condition, i) => (
              <Fragment key={condition.meta.name}>
                <Divider />
                <Grid container sx={{ alignItems: "center", width: "100%" }}>
                  <Filter
                    condition={condition}
                    chartOfAccountItems={chartOfAccountItems}
                    parentNode={currentNode}
                    index={i}
                  />
                </Grid>
              </Fragment>
            ))}
        </PropertyItemContainer>
      </Stack>
    </Grid>
  );
};

export default VariablePropertyItem;
